import React, { useCallback, useState } from 'react';

import { AutocompleteInputChangeReason, Fade, MenuItem, MenuList, useAutocomplete } from '@mui/material';

import { emptyArray } from 'app/constants';
import useRefSetter from 'hooks/use-ref-setter';

import useStyles from './styles';

import { ChangeTrigger } from '../../types';
import BaseInput from '..//BaseInput';
import HighlightSubstring from '..//HighlightSubstring';

interface Props {
    id?: string;
    placeholder?: string;
    className?: string;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    onChange?: (value: string, trigger: ChangeTrigger) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    value?: string;
    options?: Option[];
    onOptionClick?: (option: Option, event: React.MouseEvent<HTMLLIElement>) => void;
    error?: string;
    loading?: boolean;
    classes?: {
        root?: string;
        input?: string;
        list?: string;
        option?: string;
        matched?: string;
        highlight?: string;
    };
    'data-testid'?: string;
}

interface Option {
    value: string;
}

const reasonToTriggerMap: Record<AutocompleteInputChangeReason, ChangeTrigger> = {
    clear: ChangeTrigger.select,
    reset: ChangeTrigger.select,
    input: ChangeTrigger.input
};

const transitionTimeout = { enter: 150, exit: 50 };

const AutoCompleteInput: React.ForwardRefRenderFunction<HTMLInputElement, Props> = (
    {
        id,
        onChange,
        onKeyDown,
        value,
        placeholder,
        options = emptyArray,
        error,
        className,
        classes,
        loading,
        startAdornment,
        endAdornment,
        onOptionClick,
        'data-testid': dataTestId
    },
    inputRef
) => {
    const { classes: internalClasses, cx } = useStyles();
    const [isTyping, setIsTyping] = useState(false);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent, value: string, reason: AutocompleteInputChangeReason) => {
            if (event) {
                onChange?.(value, reasonToTriggerMap[reason] || ChangeTrigger.input);
            }
        },
        [onChange]
    );

    const { getRootProps, getInputProps, getListboxProps, groupedOptions, getOptionProps, popupOpen } = useAutocomplete(
        {
            id,
            options,
            freeSolo: true,
            inputValue: value,
            onHighlightChange: (_event, option, reason) => setIsTyping(!option || reason !== 'keyboard'),
            onInputChange: handleInputChange,
            getOptionLabel: (it) => (typeof it === 'string' ? it : it.value)
        }
    );
    const inputProps = getInputProps();
    const setRef = useRefSetter(inputProps.ref, inputRef);
    return (
        <div
            {...getRootProps()}
            className={cx(internalClasses.root, classes?.root, { [internalClasses.error]: !!error }, className)}
        >
            <BaseInput
                placeholder={placeholder}
                className={classes?.input}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                {...(inputProps as object)}
                ref={setRef}
                onKeyDown={isTyping ? onKeyDown : inputProps.onKeyDown}
                error={error}
                data-testid={dataTestId}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
            />
            <Fade in={popupOpen && (groupedOptions.length > 0 || loading)} timeout={transitionTimeout}>
                <MenuList {...getListboxProps()} className={cx(internalClasses.list, classes?.list)}>
                    {groupedOptions.map((option, index) => {
                        const optionProps = getOptionProps({ option, index });
                        return (
                            <MenuItem<'li'>
                                key={index}
                                {...optionProps}
                                {...option}
                                className={cx(internalClasses.option, classes?.option)}
                                onClick={(event) => {
                                    optionProps.onClick?.(event);
                                    onOptionClick?.(option, event);
                                }}
                            >
                                <HighlightSubstring
                                    source={option.value}
                                    substring={value}
                                    classes={{
                                        matched: cx(internalClasses.matched, classes?.matched),
                                        highlight: cx(internalClasses.highlight, classes?.highlight)
                                    }}
                                />
                            </MenuItem>
                        );
                    })}
                    {groupedOptions.length === 0 && loading ? (
                        <MenuItem disabled className={cx(internalClasses.option, classes?.option)}>
                            {'Loading...'}
                        </MenuItem>
                    ) : null}
                </MenuList>
            </Fade>
        </div>
    );
};

export default React.memo(React.forwardRef(AutoCompleteInput));
